import { Workbook } from "exceljs";
import * as ExcelJS from "exceljs";
import { saveAs } from "file-saver-es";
import { exportDataGrid as exportDataGridExcel } from "devextreme/excel_exporter";
import { exportDataGrid as exportDataGridPdf } from "devextreme/pdf_exporter";
import { jsPDF } from "jspdf";
import { DevExpressExportProps } from "components/common/DevExpressReport/DevExpressExportProps";
import { ExportingEvent } from "devextreme/ui/data_grid";

export class DevExpressService {
  static exportCsv = (e: ExportingEvent<any, any>, props: DevExpressExportProps) => {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet("Main sheet");

    // determine how many rows we need to insert before the data for header and filters
    let insertingRows: number = 1; // why this must start at 1?
    if (props.header) insertingRows++;
    if (props.tenantName) insertingRows++;
    if (props.infoTuples && props.infoTuples.length > 0) insertingRows = insertingRows + props.infoTuples.length;

    exportDataGridExcel({
      component: e.component,
      worksheet,
      autoFilterEnabled: true,
      topLeftCell: { row: insertingRows + 1, column: 1 }, // + 1 for padding
      customizeCell: ({ gridCell, excelCell }) => {
        if (gridCell?.rowType === 'data') {
          if (gridCell.column?.dataType === 'date' || gridCell.value instanceof Date) {
            const date = gridCell.value as Date;
            if (date) {
              excelCell.value = date.toLocaleDateString('en-US');
            }
          }
        }
      }
    }).then((cellRange) => {
      const maxColumns: number | undefined = cellRange.to?.column;

      let currentRow = 1;
      if (props.header) {
        const row = worksheet.getRow(currentRow);
        row.getCell(1).value = props.header; // Set header text only in the first cell
        for (let i = 2; i <= maxColumns!; i++) {
          row.getCell(i).value = "";
        }
        currentRow++;
      }

      if (props.tenantName) {
        const row = worksheet.getRow(currentRow);
        row.getCell(1).value = props.tenantName; // Set header text only in the first cell
        for (let i = 2; i <= maxColumns!; i++) {
          row.getCell(i).value = "";
        }
        currentRow++;
      }

      if (props.infoTuples) {
        for (let i in props.infoTuples) {
          const infoTuple: [string, string | undefined] = props.infoTuples[i];
          const row = worksheet.getRow(currentRow);

          row.getCell(1).value = infoTuple[0] + ": " + infoTuple[1];
          for (let i = 2; i <= maxColumns!; i++) {
            row.getCell(i).value = "";
          }
          currentRow++;
        }
      }

      // if (insertingRows > 1) {
      //   worksheet.mergeCells(currentRow, 1, currentRow, maxColumns!);
      // }

      workbook.csv.writeBuffer().then((buffer) => {
        saveAs(new Blob([buffer], { type: "text/csv" }), `${props.filename}.csv`);
      });
    });

    e.cancel = true;
  };

  static exportExcel = (e: ExportingEvent<any, any>, props: DevExpressExportProps) => {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet("Main sheet");

    // determine how many rows we need to insert before the data for header and filters
    let insertingRows: number = 1; // why this must start at 1?
    if (props.header) insertingRows++;
    if (props.tenantName) insertingRows++;
    if (props.infoTuples && props.infoTuples.length > 0) insertingRows = insertingRows + props.infoTuples.length;
    const hasExportMetadata = insertingRows > 1;

    exportDataGridExcel({
      component: e.component,
      worksheet,
      autoFilterEnabled: true,
      topLeftCell: { row: hasExportMetadata ? insertingRows + 1 : 1, column: 1 },
      customizeCell: ({ gridCell, excelCell }) => {
        if (gridCell?.rowType === 'data') {
          if (gridCell.column?.dataType === 'date' || gridCell.value instanceof Date) {
            const date = gridCell.value as Date;
            if (date) {
              excelCell.value = date.toLocaleDateString('en-US');

              // Urban wants "Date" types to be formatted as "General" in Excel, this is done by removing any formatting
              excelCell.numFmt = undefined;
            }
          }
        }
      }
    }).then((cellRange) => {
      const maxColumns: number | undefined = cellRange.to?.column;

      let currentRow = 1;
      if (props.header) {
        const row = worksheet.getRow(currentRow);
        row.height = 50;
        worksheet.mergeCells(currentRow, 1, currentRow, maxColumns!);

        row.getCell(1).value = props.header;
        row.getCell(1).font = { size: 22, bold: true };
        row.getCell(1).alignment = { vertical: "top" };
        currentRow++;
      }

      if (props.tenantName) {
        const row = worksheet.getRow(currentRow);
        row.height = 30;
        worksheet.mergeCells(currentRow, 1, currentRow, maxColumns!);

        row.getCell(1).value = props.tenantName;
        row.getCell(1).font = { size: 16, bold: true };
        row.getCell(1).alignment = { vertical: "top" };
        currentRow++;
      }

      if (props.infoTuples) {
        for (let i in props.infoTuples) {
          const infoTuple: [string, string | undefined] = props.infoTuples[i];
          const row = worksheet.getRow(currentRow);
          worksheet.mergeCells(currentRow, 2, currentRow, maxColumns! >= 2 ? maxColumns! : 2);

          row.getCell(1).value = infoTuple[0] + ":";
          row.getCell(2).value = infoTuple[1];
          currentRow++;
        }
      }

      if (hasExportMetadata) {
        worksheet.mergeCells(currentRow, 1, currentRow, maxColumns!);
      }

      workbook.xlsx.writeBuffer().then((buffer) => {
        saveAs(new Blob([buffer], { type: "application/octet-stream" }), `${props.filename}.xlsx`);
      });
    });

    e.cancel = true;
  };

  static exportPdf = (e: ExportingEvent<any, any>, props: DevExpressExportProps, landscape?: boolean) => {
    const doc = new jsPDF(landscape ? "landscape" : undefined);

    const headerY = 6;
    const tenantNameY = 6;
    const infoTupleY = 5;

    let topLeftY = 0;
    if (props.header) topLeftY = topLeftY + headerY;
    if (props.tenantName) topLeftY = topLeftY + tenantNameY;
    if (props.infoTuples) topLeftY = topLeftY + props.infoTuples.length * infoTupleY;
    topLeftY = topLeftY + 10; // padding

    exportDataGridPdf({
      jsPDFDocument: doc,
      component: e.component,
      //indent: 0, // not working?
      topLeft: { x: 1, y: topLeftY },
    }).then(() => {
      doc.setPage(1);

      const indentX = 15;
      const indentY = 15;
      let currentY = indentY;

      const pageWidth = doc.internal.pageSize.getWidth();

      doc.addImage("logo.png", "JPEG", indentX, 10, 15, 15);

      if (props.header) {
        doc.setFontSize(20);
        const headerWidth = doc.getTextDimensions(props.header).w;
        doc.text(props.header, (pageWidth - headerWidth) / 2, currentY);
        currentY = currentY + headerY;
      }

      if (props.tenantName) {
        doc.setFontSize(14);
        const tenantNameWidth = doc.getTextDimensions(props.tenantName).w;
        doc.text(props.tenantName, (pageWidth - tenantNameWidth) / 2, currentY);
        currentY = currentY + tenantNameY;
      }

      // padding
      currentY = currentY + 10;

      if (props.infoTuples) {
        for (let i in props.infoTuples) {
          doc.setFontSize(12);
          const infoTuple: [string, string | undefined] = props.infoTuples[i];
          doc.text(`${infoTuple[0]}: ${infoTuple[1] ? infoTuple[1] : ""}`, indentX, currentY);
          currentY = currentY + infoTupleY;
        }
      }

      // footer
      var totalPages = doc.getNumberOfPages();
      doc.setPage(totalPages);
      doc.setFontSize(10);
      const footerText = "Powered by Compass";
      const footerWidth = doc.getTextDimensions(footerText).w;
      doc.text(footerText, (pageWidth - footerWidth) / 2, doc.internal.pageSize.height - 10);

      doc.setProperties({ title: `${props.filename}.pdf` });
      const pdfData = doc.output("blob");
      const blobUrl = URL.createObjectURL(pdfData);

      // Create an anchor element and force a download with the correct filename
      const a = document.createElement("a");
      document.body.appendChild(a);
      a.href = blobUrl;
      a.download = `${props.filename}.pdf`;
      a.click();
      document.body.removeChild(a);

      // Clean up the Blob URL
      URL.revokeObjectURL(blobUrl);
    });

    e.cancel = true;
  };
}
